/**
 * Pink noise playback module.
 *
 * Creates an AudioWorkletNode that generates pink noise and routes
 * it through a GainNode to the audio context destination.
 * The user can then route system audio to their speaker.
 */

export interface PinkNoiseNode {
  worklet: AudioWorkletNode;
  gain: GainNode;
  playing: boolean;
}

/**
 * Initialize the pink noise worklet on the given AudioContext.
 * Must be called before createPinkNoise.
 */
export async function initPinkNoiseWorklet(
  context: AudioContext
): Promise<void> {
  // Worklet runs in a separate scope — served as static JS from public/
  await context.audioWorklet.addModule("/pink-noise-processor.js");
}

/**
 * Create a pink noise generator node.
 * Call initPinkNoiseWorklet first.
 *
 * @param context - The AudioContext (typically the same one used for capture)
 * @param initialGainDb - Initial gain in dBFS (default: -20)
 */
export function createPinkNoise(
  context: AudioContext,
  initialGainDb: number = -20
): PinkNoiseNode {
  const worklet = new AudioWorkletNode(context, "pink-noise-processor");
  const gain = context.createGain();
  gain.gain.value = dbToLinear(initialGainDb);

  worklet.connect(gain);
  // Don't connect to destination yet — that happens on play

  return { worklet, gain, playing: false };
}

/**
 * Start playing pink noise through the default audio output.
 */
export function playPinkNoise(
  node: PinkNoiseNode,
  context: AudioContext
): void {
  if (!node.playing) {
    node.gain.connect(context.destination);
    node.playing = true;
  }
}

/**
 * Stop playing pink noise.
 */
export function stopPinkNoise(node: PinkNoiseNode): void {
  if (node.playing) {
    node.gain.disconnect();
    node.playing = false;
  }
}

/**
 * Set the pink noise output level.
 * @param levelDb - level in dBFS
 */
export function setPinkNoiseLevel(
  node: PinkNoiseNode,
  levelDb: number
): void {
  node.gain.gain.value = dbToLinear(levelDb);
}

/**
 * Convert dBFS to linear gain.
 */
function dbToLinear(db: number): number {
  return Math.pow(10, db / 20);
}

/**
 * Clean up the pink noise node.
 */
export function destroyPinkNoise(node: PinkNoiseNode): void {
  stopPinkNoise(node);
  node.worklet.disconnect();
}
